import { getPageMeta, isProductionHost } from "@/helpers/seo"
import { useEffect } from "react"
import { useLocation } from "react-router"

const upsertMeta = (attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement("meta")
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.content = content
}

const setCanonical = (href: string | null) => {
  let el = document.head.querySelector<HTMLLinkElement>("link[rel='canonical']")
  if (!href) {
    el?.remove()
    return
  }
  if (!el) {
    el = document.createElement("link")
    el.rel = "canonical"
    document.head.appendChild(el)
  }
  el.href = href
}

/**
 * Syncs <title>, description, canonical, robots and Open Graph tags with the current route.
 * Non-production hosts (staging, localhost) are always noindex so they never compete with prod.
 */
export const usePageMeta = () => {
  const { pathname } = useLocation()

  useEffect(() => {
    const meta = getPageMeta(pathname)
    const indexable = meta.indexable && isProductionHost(window.location.hostname)

    document.title = meta.title
    upsertMeta("name", "description", meta.description)
    upsertMeta("name", "robots", indexable ? "index, follow" : "noindex, nofollow")
    upsertMeta("property", "og:title", meta.title)
    upsertMeta("property", "og:description", meta.description)
    upsertMeta("property", "og:url", meta.canonical ?? window.location.href)
    upsertMeta("name", "twitter:title", meta.title)
    upsertMeta("name", "twitter:description", meta.description)
    setCanonical(meta.canonical)
  }, [pathname])
}
